import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { cfpBaseUrl } from '../../network/urls';
import { resolveApiOfficeCode } from '../../utils/adminCompany';

const initialState = {
    data: [],
    pageNumber: 1,
    pageSize: 9999,
    subDivisionCode: '',
    dateFrom: '',
    dateTo: '',
    totalRecords: '',
    totalPages: null,
    error: null,
    isLoading: false,
}

const isSubdivisionOfficeCode = (officeCode) => {
    const codeLength = String(officeCode ?? '').trim().length;
    return codeLength === 5;
};

export const fetchPaginatedAllComplaints = createAsyncThunk(
    'complaints/fetchAllComplaints',
    async ({ pageSize, pageNumber, subDivisionCode, dateFrom, dateTo, roleTypeId }, { getState }) => {
        const { auth } = getState();
        const { token, code, roleId, selectedAdminCompanyCode } = auth;

        let codeToAPI = subDivisionCode;

        // Admin company-level (2-digit disco only); circle/division/subdivision codes pass through
        if (roleTypeId == 1 && String(subDivisionCode).length <= 2) {
            codeToAPI = subDivisionCode.toString().slice(0, 2);
        }

        const resolvedOfficeCode = resolveApiOfficeCode(
            code,
            roleId,
            codeToAPI,
            selectedAdminCompanyCode,
        );
        const useSubdivisionApi = isSubdivisionOfficeCode(resolvedOfficeCode);
        const today = new Date().toISOString().split('T')[0];
        const apiDateFrom = dateFrom || today;
        const apiDateTo = dateTo || today;

        const myHeaders = new Headers();
        myHeaders.append("Content-Type", "application/json");
        myHeaders.append("Authorization", token);

        const raw = JSON.stringify({
            pageNumber: String(pageNumber ?? 1),
            pageSize: String(pageSize ?? 1000),
            SubDivisionCode: resolvedOfficeCode,
            dateFrom: useSubdivisionApi ? "" : apiDateFrom,
            dateTo: apiDateTo,
        });

        const requestOptions = {
            method: "POST",
            headers: myHeaders,
            body: raw,
            redirect: "follow"
        };

        // Subdivision (5-digit): WithoutStatus; company/circle/division: hierarchical
        const endpoint = useSubdivisionApi
            ? 'lineComplaints/GetAllComplaintsBySubDivisionWithoutStatus'
            : 'lineComplaints/GetAllComplaintsByHierarchicalWithRegisteredAt';

        const response = await fetch(cfpBaseUrl + endpoint, requestOptions);

        if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.Message || errorData.message || 'Complaints API issue!');
        }

        const result = await response.json();
        if (result.Message === "Data Not Found.") {
            return { data: [], totalRecords: 0, totalPages: 0 };
        }

        const complaintsData = Array.isArray(result.data) ? result.data : [];

        return {
            data: complaintsData,
            totalRecords: result.totalRecords ?? complaintsData.length,
            totalPages: result.totalPages ?? 1,
        };
    }
);

const getAllComplaintsPaginatedSlice = createSlice({
    name: 'getAllComplaints',
    initialState: initialState,
    reducers: {
        nextPage(state) {
            state.pageNumber += 1;
        },
        previousPage(state) {
            if (state.pageNumber > 1) {
                state.pageNumber -= 1;
            }
        },
        firstPage(state) {
            state.pageNumber = 1;
        },
        lastPage(state, action) {
            state.pageNumber = action.payload;
        },
        resetState(state) {
            state.data = []
            state.pageNumber = 1
            state.pageSize = 9999
            state.subDivisionCode = ''
            state.dateFrom = ''
            state.dateTo = ''
            state.totalRecords = ''
            state.totalPages = null
            state.error = null
        },
    },

    extraReducers: (builder) => {
        builder
            .addCase(fetchPaginatedAllComplaints.pending, (state, action) => {
                state.isLoading = true;
                state.error = null;
                const nextCode = action.meta?.arg?.subDivisionCode?.toString() || '';
                const prevCode = state.subDivisionCode?.toString() || '';
                // Company/office switch: drop stale list immediately so UI does not keep old DISCO
                if (nextCode !== prevCode) {
                    state.data = [];
                    state.subDivisionCode = nextCode;
                    state.totalRecords = 0;
                    state.totalPages = null;
                }
            })
            .addCase(fetchPaginatedAllComplaints.fulfilled, (state, action) => {
                const responseCode = action.meta?.arg?.subDivisionCode?.toString() || '';
                // Ignore late responses from a previous company/office request
                if (
                    state.subDivisionCode &&
                    responseCode &&
                    state.subDivisionCode.toString() !== responseCode
                ) {
                    return;
                }
                state.isLoading = false;
                state.data = action.payload?.data || [];
                state.totalPages = action.payload?.totalPages || 1;
                state.totalRecords = action.payload?.totalRecords ?? 0;
                state.subDivisionCode = responseCode;
                state.error = null;
            })
            .addCase(fetchPaginatedAllComplaints.rejected, (state, action) => {
                state.isLoading = false;
                // Keep last good list on refresh failure — do not wipe a successful parallel response
                state.error = action.error?.message || "Unable to fetch complaints";
            });
    }
});

export const { nextPage, previousPage, firstPage, lastPage, resetState } = getAllComplaintsPaginatedSlice.actions;

export default getAllComplaintsPaginatedSlice.reducer;
